import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Popper,
  Select,
  TextField,
} from '@mui/material';
import type {
  AutocompleteInputChangeReason,
  AutocompleteRenderInputParams,
  AutocompleteRenderOptionState,
} from '@mui/material/Autocomplete';
import { enqueueSnackbar } from 'notistack';
import { GlobalContext } from '../../../providers/GlobalProvider';
import {
  useCheckEntityDuplicate,
  useCreateEntity,
  useGetEntitiesByDatabaseId,
  useGetEntitiesByGroupId,
} from 'api/bitedb/entities/entities.hooks';
import { useGetDatabasesByGroupId } from 'api/bitedb/database/database.hooks';
import { useCreateEntityRelationship } from 'api/bitedb/entityRelationships/entityRelationships.hooks';
import type { IEntityRelationshipWithNames } from 'api/bitedb/entityRelationships/entityRelationships.d';
import type { IDatabase } from 'api/bitedb/database/database';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  sourceEntityId?: string;
  sourceEntityName?: string;
  sourceDatabaseId?: string;
  sourceDatabaseName?: string;
  existingRelationships?: IEntityRelationshipWithNames[];
  isLoadingExistingRelationships?: boolean;
  onSuccess?: () => void;
}

type RelationshipEntity = {
  id?: string;
  entity_id?: string;
  name?: string;
  entity_type?: string;
  job_title?: string;
  category?: string;
  subcategory?: string;
  country_of_origin?: string;
  database_id?: string;
  database_name?: string;
};

type RelationshipTargetKind = 'company' | 'person';
type ModalMode = 'existing' | 'create';

type CreateEntityFormState = {
  name: string;
  alias: string;
  description: string;
  email: string;
  phone: string;
  address: string;
  address1: string;
  address2: string;
  country: string;
  businessUnit: string;
  parentBusiness: string;
  skuProductId: string;
  supplierVendorId: string;
  businessRegistrationNumber: string;
  personId: string;
  manufacturerIdentificationNumber: string;
  jobTitle: string;
};

const COMPANY_RELATIONSHIP_TYPES = [
  'parent_company',
  'subsidiary',
  'division',
  'franchise',
  'branch_office',
];

const PERSON_RELATIONSHIP_TYPES = [
  'director',
  'chairperson',
  'ceo',
  'cfo',
  'coo',
  'cto',
  'president',
  'employee',
  'founder',
  'partner',
  'shareholder',
  'board_member',
];

const RELATIONSHIP_TYPES = [
  ...COMPANY_RELATIONSHIP_TYPES,
  ...PERSON_RELATIONSHIP_TYPES,
];

const RELATIONSHIP_TYPE_LABELS: Record<string, string> = {
  ceo: 'CEO',
  cfo: 'CFO',
  coo: 'COO',
  cto: 'CTO',
};

const EMPTY_CREATE_FORM: CreateEntityFormState = {
  name: '',
  alias: '',
  description: '',
  email: '',
  phone: '',
  address: '',
  address1: '',
  address2: '',
  country: '',
  businessUnit: '',
  parentBusiness: '',
  skuProductId: '',
  supplierVendorId: '',
  businessRegistrationNumber: '',
  personId: '',
  manufacturerIdentificationNumber: '',
  jobTitle: '',
};

const tariffInputSx = {
  background: 'var(--Background-200, var(--input-color))',
  borderRadius: '8px',
  width: '100%',
  minWidth: '87px',
  '& .MuiInputBase-root': {
    width: '100%',
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    height: '45px',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--secondary-color) !important',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--secondary-color) !important',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--secondary-color) !important',
    },
    color: 'var(--input-text-color)',
    boxShadow: 'none',
    paddingRight: '10px',
  },
  '& .MuiInputBase-input': {
    color: 'var(--Text-Main, var(--input-text-color))',
    fontFamily: 'var(--font-family)',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '24px',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'var(--Text-Muted, #9CA3AF)',
    opacity: 1,
  },
  '& .MuiInputBase-input.Mui-disabled': {
    color: 'var(--Text-Muted, #9CA3AF)',
    WebkitTextFillColor: 'var(--Text-Muted, #9CA3AF)',
    opacity: 1,
  },
  '& .MuiSelect-select.Mui-disabled': {
    color: 'var(--Text-Muted, #9CA3AF)',
    WebkitTextFillColor: 'var(--Text-Muted, #9CA3AF)',
    opacity: 1,
  },
  '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--secondary-color)',
    opacity: 0.7,
  },
  '& .MuiOutlinedInput-root.Mui-disabled .MuiSvgIcon-root': {
    color: 'var(--Text-Muted, #9CA3AF)',
  },
  '& .MuiSvgIcon-root': {
    color: 'var(--input-text-color)',
  },
};

const tariffAutocompleteSx = {
  background: 'var(--Background-200, var(--input-color))',
  borderRadius: '8px',
  height: '45px',
  '& .MuiAutocomplete-input': {
    color: 'var(--Text-Main, var(--input-text-color))',
    fontFamily: 'var(--font-family)',
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '24px',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    border: 'none',
  },
  '& .MuiInputBase-input::placeholder': {
    color: 'var(--Text-Muted, #9CA3AF)',
    opacity: 1,
  },
  '& .MuiInputBase-input.Mui-disabled': {
    color: 'var(--Text-Muted, #9CA3AF)',
    WebkitTextFillColor: 'var(--Text-Muted, #9CA3AF)',
    opacity: 1,
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    height: '45px',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--secondary-color) !important',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--secondary-color) !important',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'var(--secondary-color) !important',
    },
    color: 'var(--input-text-color)',
    boxShadow: 'none',
  },
  '& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--secondary-color)',
    opacity: 0.7,
  },
  '& .MuiOutlinedInput-root.Mui-disabled .MuiSvgIcon-root': {
    color: 'var(--Text-Muted, #9CA3AF)',
  },
  '& .MuiSvgIcon-root': {
    color: 'var(--input-text-color)',
  },
};

const themedMenuProps = {
  PaperProps: {
    sx: {
      mt: '6px',
      borderRadius: '8px',
      border: '1px solid var(--secondary-color)',
      background: 'var(--Background-200, var(--input-color))',
      color: 'var(--Text-Main, var(--input-text-color))',
      '& .MuiMenuItem-root': {
        color: 'var(--Text-Main, var(--input-text-color))',
        fontFamily: 'var(--font-family)',
        fontSize: '14px',
      },
      '& .MuiMenuItem-root:hover': {
        backgroundColor: 'rgba(72, 149, 239, 0.12)',
      },
      '& .MuiMenuItem-root.Mui-selected': {
        backgroundColor: 'rgba(72, 149, 239, 0.2)',
      },
      '& .MuiMenuItem-root.Mui-selected:hover': {
        backgroundColor: 'rgba(72, 149, 239, 0.26)',
      },
    },
  },
};
const modeToggleSx = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: '100%',
  maxWidth: '100%',
  marginBottom: '20px',
  padding: '6px',
  borderRadius: '12px',
  border: '1px solid var(--secondary-color)',
  background: 'var(--Background-100, var(--bg-colors))',
};

const modeTabButtonSx = (isActive: boolean) => ({
  flex: 1,
  minHeight: '44px',
  border: isActive
    ? '1px solid rgba(72, 149, 239, 0.45)'
    : '1px solid var(--secondary-color)',
  background: isActive
    ? 'linear-gradient(180deg, rgba(72, 149, 239, 0.28) 0%, rgba(53, 102, 163, 0.4) 100%)'
    : 'var(--Background-200, var(--input-color))',
  color: isActive
    ? 'var(--text-primary)'
    : 'var(--Text-Muted, var(--card-text))',
  borderRadius: '8px',
  padding: '0 18px',
  fontSize: '14px',
  fontWeight: 700,
  lineHeight: 1,
  whiteSpace: 'nowrap' as const,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  boxShadow: isActive ? '0 8px 20px rgba(43, 93, 153, 0.22)' : 'none',
  transition: 'all 0.2s ease',
});

const databasePanelSx = {
  width: '100%',
  borderRadius: '10px',
  border: '1px solid var(--secondary-color)',
  background: 'rgba(72, 149, 239, 0.08)',
  padding: '14px 16px',
};

const databaseBadgeSx = {
  borderRadius: '999px',
  border: '1px solid rgba(72, 149, 239, 0.35)',
  padding: '2px 8px',
  color: 'var(--Primary-400, #4895ef)',
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
};

const formatRelationshipType = (value: string) =>
  value
    .split('_')
    .map((word) => {
      const normalizedWord = word.toLowerCase();
      return (
        RELATIONSHIP_TYPE_LABELS[normalizedWord] ||
        normalizedWord.charAt(0).toUpperCase() + normalizedWord.slice(1)
      );
    })
    .join(' ');

const getEntityMeta = (entity: RelationshipEntity) => {
  const parts: string[] = [];

  if (entity?.job_title) {
    parts.push('Person');
    parts.push(entity.job_title);
  }
  if (entity?.category) {
    parts.push(entity.category);
  }
  if (entity?.subcategory) {
    parts.push(entity.subcategory);
  }
  if (entity?.country_of_origin) {
    parts.push(entity.country_of_origin);
  }

  return parts.filter(Boolean).join(' - ');
};

const getRelationshipEntityId = (entity?: RelationshipEntity | null) =>
  entity?.entity_id || entity?.id || '';

const AddEntityRelationshipModal: React.FC<Props> = ({
  isOpen,
  onClose,
  sourceEntityId,
  sourceEntityName,
  sourceDatabaseId,
  sourceDatabaseName,
  existingRelationships = [],
  isLoadingExistingRelationships = false,
  onSuccess,
}) => {
  const { cur_user } = useContext(GlobalContext);
  const groupId = cur_user?.selected_team?.id;

  const [modalMode, setModalMode] = useState<ModalMode>('existing');
  const [relationshipSearch, setRelationshipSearch] = useState('');
  const [relationshipTarget, setRelationshipTarget] =
    useState<RelationshipEntity | null>(null);
  const [relationshipTargetKind, setRelationshipTargetKind] = useState<
    RelationshipTargetKind | ''
  >('');
  const [relationshipType, setRelationshipType] = useState('');
  const [createForm, setCreateForm] =
    useState<CreateEntityFormState>(EMPTY_CREATE_FORM);
  const [createDatabaseId, setCreateDatabaseId] = useState('');
  const [isRelationshipSaving, setIsRelationshipSaving] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const createEntityRelationshipMutation = useCreateEntityRelationship();
  const createEntityMutation = useCreateEntity();
  const checkDuplicateMutation = useCheckEntityDuplicate();

  const relationshipPagination = useMemo(
    () => ({
      page: 1,
      limit: 200,
      sortBy: 'name',
      sortOrder: 'asc' as const,
      search: '',
      databaseName: '',
    }),
    [],
  );

  const {
    data: databaseRelationshipEntities = [],
    isLoading: isDatabaseRelationshipLoading,
    error: databaseRelationshipEntitiesError,
  } = useGetEntitiesByDatabaseId(
    isOpen && sourceDatabaseId ? sourceDatabaseId : '',
    relationshipSearch,
    relationshipPagination,
  );

  const {
    data: groupRelationshipEntities = [],
    isLoading: isGroupRelationshipLoading,
    error: groupRelationshipEntitiesError,
  } = useGetEntitiesByGroupId(
    isOpen && !sourceDatabaseId ? groupId || '' : '',
    relationshipSearch,
    relationshipPagination,
  );

  const relationshipEntities = sourceDatabaseId
    ? databaseRelationshipEntities
    : groupRelationshipEntities;
  const isRelationshipLoading = sourceDatabaseId
    ? isDatabaseRelationshipLoading
    : isGroupRelationshipLoading;
  const relationshipEntitiesError = sourceDatabaseId
    ? databaseRelationshipEntitiesError
    : groupRelationshipEntitiesError;

  const { data: databases = [], isLoading: isLoadingDatabases } =
    useGetDatabasesByGroupId(isOpen ? groupId || '' : '');

  const availableEntityDatabases = useMemo(
    () =>
      (databases || []).filter(
        (database: IDatabase) => database?.type === 'people & companies',
      ),
    [databases],
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    if (sourceDatabaseId) {
      setCreateDatabaseId(sourceDatabaseId);
      return;
    }

    if (!createDatabaseId && availableEntityDatabases.length === 1) {
      setCreateDatabaseId(availableEntityDatabases[0]?.id || '');
    }
  }, [availableEntityDatabases, createDatabaseId, isOpen, sourceDatabaseId]);

  const effectiveTargetDatabaseId = sourceDatabaseId || createDatabaseId;
  const effectiveTargetDatabaseName = useMemo(() => {
    if (sourceDatabaseName) {
      return sourceDatabaseName;
    }

    if (!effectiveTargetDatabaseId) {
      return '';
    }

    return (
      availableEntityDatabases.find(
        (database) => database?.id === effectiveTargetDatabaseId,
      )?.name || ''
    );
  }, [availableEntityDatabases, effectiveTargetDatabaseId, sourceDatabaseName]);

  const linkedEntityIds = useMemo(() => {
    const linkedIds = new Set<string>();

    if (!sourceEntityId || !Array.isArray(existingRelationships)) {
      return linkedIds;
    }

    existingRelationships.forEach((relationship) => {
      if (relationship?.is_active === false) {
        return;
      }

      const sourceId = relationship?.source_entity_id;
      const targetId = relationship?.target_entity_id;

      if (!sourceId || !targetId) {
        return;
      }

      if (sourceId === sourceEntityId && targetId !== sourceEntityId) {
        linkedIds.add(targetId);
      }
      if (targetId === sourceEntityId && sourceId !== sourceEntityId) {
        linkedIds.add(sourceId);
      }
    });

    return linkedIds;
  }, [existingRelationships, sourceEntityId]);

  const filteredRelationshipEntities = useMemo((): RelationshipEntity[] => {
    return (relationshipEntities || [])
      .filter((entity: RelationshipEntity) => entity?.name)
      .filter((entity: RelationshipEntity) => {
        if (
          sourceDatabaseId &&
          entity?.database_id &&
          entity.database_id !== sourceDatabaseId
        ) {
          return false;
        }

        const entityId = getRelationshipEntityId(entity);
        if (!entityId || entityId === sourceEntityId) {
          return false;
        }
        return !linkedEntityIds.has(entityId);
      });
  }, [linkedEntityIds, relationshipEntities, sourceDatabaseId, sourceEntityId]);

  const filteredRelationshipTypes = useMemo(() => {
    if (relationshipTargetKind === 'person') {
      return PERSON_RELATIONSHIP_TYPES;
    }
    if (relationshipTargetKind === 'company') {
      return COMPANY_RELATIONSHIP_TYPES;
    }
    return RELATIONSHIP_TYPES;
  }, [relationshipTargetKind]);

  const isCreateMode = modalMode === 'create';
  const showDropdown =
    !isCreateMode &&
    !!relationshipTargetKind &&
    isDropdownOpen &&
    relationshipSearch.trim().length > 0;
  const selectedRelationshipTargetId =
    getRelationshipEntityId(relationshipTarget);
  const isDuplicateSelection =
    !!selectedRelationshipTargetId &&
    linkedEntityIds.has(selectedRelationshipTargetId);

  const canSaveRelationship = isCreateMode
    ? !!sourceEntityId &&
      !!effectiveTargetDatabaseId &&
      !!relationshipTargetKind &&
      !!relationshipType &&
      !!createForm.name.trim() &&
      !isRelationshipSaving &&
      !createEntityMutation.isPending &&
      !checkDuplicateMutation.isPending
    : !!sourceEntityId &&
      !!selectedRelationshipTargetId &&
      !!relationshipTargetKind &&
      !!relationshipType &&
      !isRelationshipSaving &&
      !isLoadingExistingRelationships &&
      !isDuplicateSelection;

  const resetForm = () => {
    setModalMode('existing');
    setRelationshipSearch('');
    setRelationshipTarget(null);
    setRelationshipTargetKind('');
    setRelationshipType('');
    setCreateForm(EMPTY_CREATE_FORM);
    setCreateDatabaseId(sourceDatabaseId || '');
    setIsDropdownOpen(false);
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  const handleModeChange = (nextMode: ModalMode) => {
    setModalMode(nextMode);
    setRelationshipTarget(null);
    setRelationshipSearch('');
    setIsDropdownOpen(false);
  };

  const handleTargetKindChange = (nextValue: RelationshipTargetKind | '') => {
    setRelationshipTargetKind(nextValue);
    setRelationshipTarget(null);
    setRelationshipSearch('');
    setRelationshipType('');
    setIsDropdownOpen(false);
    if (nextValue !== 'person') {
      setCreateForm((prev) => ({
        ...prev,
        jobTitle: '',
      }));
    }
  };

  const handleRelationshipTypeChange = (nextValue: string) => {
    const nextKind = PERSON_RELATIONSHIP_TYPES.includes(nextValue)
      ? 'person'
      : COMPANY_RELATIONSHIP_TYPES.includes(nextValue)
      ? 'company'
      : '';

    setRelationshipType(nextValue);

    if (nextKind && nextKind !== relationshipTargetKind) {
      setRelationshipTargetKind(nextKind);
      setRelationshipTarget(null);
      setRelationshipSearch('');
      setIsDropdownOpen(false);
      if (nextKind !== 'person') {
        setCreateForm((prev) => ({
          ...prev,
          jobTitle: '',
        }));
      }
    }
  };

  const handleCreateFieldChange = (
    field: keyof CreateEntityFormState,
    value: string,
  ) => {
    setCreateForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreateTextFieldChange =
    (field: keyof CreateEntityFormState) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      handleCreateFieldChange(field, event.target.value);
    };

  const switchToExistingSearch = (searchValue: string) => {
    setModalMode('existing');
    setRelationshipTarget(null);
    setRelationshipSearch(searchValue);
    setIsDropdownOpen(
      !!relationshipTargetKind && searchValue.trim().length > 0,
    );
  };

  const persistRelationship = async (
    targetEntityId: string,
    successMessage: string,
    partialFailureMessage?: string,
  ) => {
    const resp = await createEntityRelationshipMutation.mutateAsync({
      payload: {
        source_entity_id: sourceEntityId || '',
        target_entity_id: targetEntityId,
        relationship_type: relationshipType,
        is_active: true,
      },
      teamId: groupId,
    });

    if (resp?.success) {
      enqueueSnackbar(successMessage, {
        variant: 'success',
      });
      handleClose();
      onSuccess?.();
      return;
    }

    enqueueSnackbar(
      resp?.message || partialFailureMessage || 'Error creating relationship',
      {
        variant: 'error',
      },
    );
  };

  const handleSaveRelationship = async () => {
    if (!sourceEntityId) {
      enqueueSnackbar('Source entity not found', { variant: 'error' });
      return;
    }

    if (!relationshipTargetKind) {
      enqueueSnackbar('Select whether this is a company or a person', {
        variant: 'error',
      });
      return;
    }

    if (!relationshipType) {
      enqueueSnackbar('Select a relationship type', { variant: 'error' });
      return;
    }

    if (!groupId) {
      enqueueSnackbar('Team context not found', { variant: 'error' });
      return;
    }

    if (isCreateMode) {
      const trimmedName = createForm.name.trim();
      const trimmedCountry = createForm.country.trim();

      if (!trimmedName) {
        enqueueSnackbar('Please provide a name for the entity', {
          variant: 'error',
        });
        return;
      }

      if (!effectiveTargetDatabaseId) {
        enqueueSnackbar(
          'Select the database where the entity should be added',
          {
            variant: 'error',
          },
        );
        return;
      }

      try {
        setIsRelationshipSaving(true);

        try {
          const duplicateResult = await checkDuplicateMutation.mutateAsync({
            payload: {
              name: trimmedName,
              country_of_origin: trimmedCountry || undefined,
              database_id: effectiveTargetDatabaseId,
            },
            teamId: groupId,
          });
          const duplicates = Array.isArray(duplicateResult?.duplicates)
            ? duplicateResult.duplicates
            : [];

          if (duplicates.length > 0) {
            enqueueSnackbar(
              `${duplicates.length} potential duplicate${
                duplicates.length > 1 ? 's' : ''
              } detected. Search existing records instead.`,
              { variant: 'warning' },
            );
            switchToExistingSearch(trimmedName);
            return;
          }
        } catch (error) {
          console.error('Error checking duplicates before add:', error);
        }

        const entityKindLabel =
          relationshipTargetKind === 'person' ? 'Person' : 'Company';
        const timestamp = new Date().toISOString();

        const createResp = await createEntityMutation.mutateAsync({
          entityData: {
            name: trimmedName,
            alias: createForm.alias.trim() || undefined,
            description: createForm.description.trim(),
            email: createForm.email.trim(),
            phone: createForm.phone.trim(),
            address: createForm.address.trim(),
            address1: createForm.address1.trim() || undefined,
            address2: createForm.address2.trim() || undefined,
            country_of_origin: trimmedCountry,
            country_of_origin_confidence: 'High',
            risk_level: 'Low',
            category: entityKindLabel,
            subCategory: entityKindLabel,
            subcategory: entityKindLabel,
            entity_type: relationshipTargetKind,
            job_title:
              relationshipTargetKind === 'person'
                ? createForm.jobTitle.trim()
                : undefined,
            created_at: timestamp,
            updated_at: timestamp,
            business_unit: createForm.businessUnit.trim(),
            parent_business: createForm.parentBusiness.trim(),
            sku_product_id: createForm.skuProductId.trim(),
            supplier_vendor_id: createForm.supplierVendorId.trim(),
            business_registration_number:
              createForm.businessRegistrationNumber.trim(),
            person_id: createForm.personId.trim(),
            manufacturer_identification_number:
              createForm.manufacturerIdentificationNumber.trim(),
            database_id: effectiveTargetDatabaseId,
            created_by: cur_user?.basics?.id,
          },
          teamId: groupId,
        });

        const createDuplicates = Array.isArray(createResp?.duplicates)
          ? createResp.duplicates
          : [];

        if (createDuplicates.length > 0) {
          enqueueSnackbar(
            `${createDuplicates.length} potential duplicate${
              createDuplicates.length > 1 ? 's' : ''
            } detected. Search existing records instead.`,
            { variant: 'warning' },
          );
          switchToExistingSearch(trimmedName);
          return;
        }

        const createdEntityId =
          createResp?.data?.entity?.id ||
          createResp?.data?.database_entity?.entity_id;

        if (!createResp?.success || !createdEntityId) {
          enqueueSnackbar(createResp?.message || 'Error creating entity', {
            variant: 'error',
          });
          return;
        }

        await persistRelationship(
          createdEntityId,
          `Entity created in ${
            effectiveTargetDatabaseName || 'the selected database'
          } and linked successfully`,
          `Entity created in ${
            effectiveTargetDatabaseName || 'the selected database'
          } but the relationship could not be saved`,
        );
      } catch (error: any) {
        console.error('Error creating entity relationship:', error);
        enqueueSnackbar(
          error?.response?.data?.message ||
            error?.message ||
            'Error creating entity relationship',
          { variant: 'error' },
        );
      } finally {
        setIsRelationshipSaving(false);
      }

      return;
    }

    if (isLoadingExistingRelationships) {
      enqueueSnackbar('Loading existing relationships. Please try again.', {
        variant: 'warning',
      });
      return;
    }

    if (!selectedRelationshipTargetId) {
      enqueueSnackbar('Select an entity to link', { variant: 'error' });
      return;
    }

    if (linkedEntityIds.has(selectedRelationshipTargetId)) {
      enqueueSnackbar(
        'An active relationship already exists between these entities',
        {
          variant: 'warning',
        },
      );
      return;
    }

    try {
      setIsRelationshipSaving(true);
      await persistRelationship(
        selectedRelationshipTargetId,
        'Relationship added successfully',
      );
    } catch (error: any) {
      console.error('Error creating relationship:', error);
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          'Error creating relationship',
        { variant: 'error' },
      );
    } finally {
      setIsRelationshipSaving(false);
    }
  };

  const DropdownPopper = (props: any) => {
    const { anchorEl, style, ...rest } = props;
    const width = anchorEl?.clientWidth;
    return (
      <Popper
        {...rest}
        anchorEl={anchorEl}
        placement='bottom-start'
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, -4],
            },
          },
        ]}
        style={{
          ...style,
          width: width || style?.width,
          zIndex: 2000,
          pointerEvents: 'auto',
        }}
      />
    );
  };

  const relationshipPreview = isCreateMode
    ? `${createForm.name.trim() || 'New entity'} will be created in ${
        effectiveTargetDatabaseName || 'the selected database'
      } and linked as ${
        relationshipType
          ? formatRelationshipType(relationshipType)
          : 'a relationship'
      } to ${sourceEntityName || 'this entity'}`
    : `${relationshipTarget?.name || 'X'} is a ${
        relationshipType
          ? formatRelationshipType(relationshipType)
          : 'relationship'
      } of ${sourceEntityName || 'Y'}`;

  return (
    <Dialog
      className='dialog_box'
      open={isOpen}
      onClose={handleClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: 'var(--bg-colors)',
          borderRadius: '0.5rem',
          overflowY: 'visible',
          maxWidth: '800px',
          width: '90%',
          margin: '0 auto',
        },
      }}
    >
      <DialogTitle className='modal-header-content modal-title'>
        Add Relationship
        <div
          className='audit-add-modal-head-cont-icon'
          onClick={handleClose}
          style={{ cursor: 'pointer' }}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='18'
            height='18'
            viewBox='0 0 18 18'
            fill='none'
          >
            <path
              d='M1.5 1.5L16.5 16.5M16.5 1.5L1.5 16.5'
              stroke='var(--text-primary)'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>
      </DialogTitle>
      <DialogContent className='dialog-content-base' style={{ paddingTop: 0 }}>
        <div
          className='audit-add-modal-cont-body'
          style={{ paddingTop: '12px', paddingBottom: '12px' }}
        >
          <div className='audit-add-modal-cont-body-frame'>
            <div className='audit-add-modal-cont-body-frame-form'>
              <div className='audit-add-modal-head-cont-supporting'>
                <h1>
                  {isCreateMode
                    ? 'Create and link entity relationship'
                    : 'Link entity relationship'}
                </h1>
                <p>{sourceEntityName || 'Entity'}</p>
              </div>

              <div style={modeToggleSx}>
                <button
                  type='button'
                  style={modeTabButtonSx(!isCreateMode)}
                  onClick={() => handleModeChange('existing')}
                >
                  Link Existing
                </button>
                <button
                  type='button'
                  style={modeTabButtonSx(isCreateMode)}
                  onClick={() => handleModeChange('create')}
                >
                  Create New
                </button>
              </div>

              <div className='audit-add-modal-cont-body-frame-form-row'>
                <div className='grid-layout' style={{ width: '100%' }}>
                  <div className='col-span-2' style={{ position: 'relative' }}>
                    <label
                      htmlFor='source-entity'
                      className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                    >
                      Source Entity
                    </label>
                    <TextField
                      id='source-entity'
                      value={sourceEntityName || 'Unknown entity'}
                      disabled
                      fullWidth
                      size='small'
                      sx={tariffInputSx}
                    />
                  </div>

                  <div className='col-span-2'>
                    <label
                      htmlFor='relationship-type'
                      className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                    >
                      Relationship Type
                    </label>
                    <Select
                      id='relationship-type'
                      value={relationshipType}
                      onChange={(e: { target: { value: string } }) =>
                        handleRelationshipTypeChange(e.target.value as string)
                      }
                      displayEmpty
                      MenuProps={themedMenuProps}
                      sx={tariffInputSx}
                      renderValue={(selected: unknown) => {
                        if (!selected) {
                          return (
                            <span
                              style={{
                                color: 'var(--Text-Muted, var(--card-text))',
                              }}
                            >
                              Select relationship type
                            </span>
                          );
                        }
                        return formatRelationshipType(selected as string);
                      }}
                    >
                      <MenuItem value=''>Select relationship type</MenuItem>
                      {filteredRelationshipTypes.map((type) => (
                        <MenuItem key={type} value={type}>
                          {formatRelationshipType(type)}
                        </MenuItem>
                      ))}
                    </Select>
                  </div>

                  <div className='col-span-2'>
                    <div style={databasePanelSx}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: '12px',
                        }}
                      >
                        <span
                          style={{
                            color: 'var(--Primary-400, #4895ef)',
                            fontSize: '12px',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                          }}
                        >
                          Target Database
                        </span>
                        {sourceDatabaseId && (
                          <span style={databaseBadgeSx}>Locked</span>
                        )}
                      </div>
                      <div
                        style={{
                          color: 'var(--text-primary)',
                          fontSize: '16px',
                          fontWeight: 600,
                          marginTop: '6px',
                        }}
                      >
                        {effectiveTargetDatabaseName ||
                          'Select a people and companies database'}
                      </div>
                      {isCreateMode && (
                        <div
                          style={{
                            color: 'var(--Text-Muted, var(--card-text))',
                            fontSize: '12px',
                            marginTop: '6px',
                          }}
                        >
                          New entities created from this modal will be added to
                          this database.
                        </div>
                      )}
                    </div>
                  </div>

                  {!sourceDatabaseId && (
                    <div className='col-span-2'>
                      <label
                        htmlFor='relationship-target-database'
                        className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                      >
                        Database
                      </label>
                      <Select
                        id='relationship-target-database'
                        value={createDatabaseId}
                        onChange={(e: { target: { value: string } }) =>
                          setCreateDatabaseId(e.target.value as string)
                        }
                        displayEmpty
                        disabled={
                          isLoadingDatabases ||
                          availableEntityDatabases.length === 0
                        }
                        MenuProps={themedMenuProps}
                        sx={tariffInputSx}
                        renderValue={(selected: unknown) => {
                          if (!selected) {
                            return (
                              <span
                                style={{
                                  color: 'var(--Text-Muted, var(--card-text))',
                                }}
                              >
                                {isLoadingDatabases
                                  ? 'Loading databases...'
                                  : 'Select database'}
                              </span>
                            );
                          }

                          return (
                            availableEntityDatabases.find(
                              (database) => database?.id === selected,
                            )?.name || 'Select database'
                          );
                        }}
                      >
                        <MenuItem value=''>Select database</MenuItem>
                        {availableEntityDatabases.map((database) => (
                          <MenuItem key={database.id} value={database.id}>
                            {database.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {availableEntityDatabases.length === 0 &&
                        !isLoadingDatabases && (
                          <div
                            style={{
                              color: 'var(--Text-Muted, var(--card-text))',
                              fontSize: '12px',
                              marginTop: '6px',
                            }}
                          >
                            No people and companies databases are available for
                            this team.
                          </div>
                        )}
                    </div>
                  )}

                  <div className='col-span-2'>
                    <label
                      htmlFor='relationship-target-kind'
                      className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                    >
                      Is this a company or a person?
                    </label>
                    <Select
                      id='relationship-target-kind'
                      value={relationshipTargetKind}
                      onChange={(e: { target: { value: string } }) =>
                        handleTargetKindChange(
                          e.target.value as RelationshipTargetKind | '',
                        )
                      }
                      displayEmpty
                      MenuProps={themedMenuProps}
                      sx={tariffInputSx}
                      renderValue={(selected: unknown) => {
                        if (!selected) {
                          return (
                            <span
                              style={{
                                color: 'var(--Text-Muted, var(--card-text))',
                              }}
                            >
                              Select company or person
                            </span>
                          );
                        }
                        return selected === 'person' ? 'Person' : 'Company';
                      }}
                    >
                      <MenuItem value=''>Select company or person</MenuItem>
                      <MenuItem value='company'>Company</MenuItem>
                      <MenuItem value='person'>Person</MenuItem>
                    </Select>
                  </div>

                  {!isCreateMode && (
                    <div className='col-span-2'>
                      <label
                        htmlFor='relationship-search'
                        className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                      >
                        Search Entity or Person
                      </label>
                      <Autocomplete<RelationshipEntity, false, false, false>
                        open={showDropdown}
                        onOpen={() => {
                          if (
                            relationshipTargetKind &&
                            relationshipSearch.trim().length > 0
                          ) {
                            setIsDropdownOpen(true);
                          }
                        }}
                        onClose={() => setIsDropdownOpen(false)}
                        options={filteredRelationshipEntities}
                        getOptionLabel={(option: RelationshipEntity) =>
                          option?.name || ''
                        }
                        isOptionEqualToValue={(
                          option: RelationshipEntity,
                          value: RelationshipEntity,
                        ) =>
                          getRelationshipEntityId(option) ===
                          getRelationshipEntityId(value)
                        }
                        filterOptions={(options: RelationshipEntity[]) =>
                          options
                        }
                        value={relationshipTarget}
                        inputValue={relationshipSearch}
                        disabled={!relationshipTargetKind}
                        onInputChange={(
                          _event: React.SyntheticEvent,
                          value: string,
                          reason: AutocompleteInputChangeReason,
                        ) => {
                          if (
                            reason === 'input' ||
                            reason === 'clear' ||
                            reason === 'reset'
                          ) {
                            setRelationshipSearch(value);
                            setIsDropdownOpen(
                              !!relationshipTargetKind &&
                                value.trim().length > 0,
                            );
                            if (reason === 'input' && relationshipTarget) {
                              setRelationshipTarget(null);
                            }
                          }
                        }}
                        onChange={(
                          _event: React.SyntheticEvent,
                          value: RelationshipEntity | null,
                        ) => {
                          setRelationshipTarget(value);
                          if (value?.name) {
                            setRelationshipSearch(value.name);
                          }
                          setIsDropdownOpen(false);
                        }}
                        loading={isRelationshipLoading}
                        noOptionsText={
                          !relationshipTargetKind
                            ? 'Select company or person first.'
                            : relationshipEntitiesError
                            ? 'Error loading records.'
                            : 'No records found.'
                        }
                        PopperComponent={DropdownPopper}
                        ListboxProps={{
                          style: {
                            maxHeight: '156px',
                            overflowY: 'auto',
                            padding: 0,
                            background:
                              'var(--Background-200, var(--input-color))',
                            color: 'var(--Text-Main, var(--input-text-color))',
                          },
                        }}
                        sx={{
                          ...tariffAutocompleteSx,
                          width: '100%',
                          '& .MuiOutlinedInput-root': {
                            ...tariffAutocompleteSx['& .MuiOutlinedInput-root'],
                            borderRadius: showDropdown ? '8px 8px 0 0' : '8px',
                          },
                        }}
                        renderOption={(
                          props: React.HTMLAttributes<HTMLLIElement>,
                          option: RelationshipEntity,
                          state: AutocompleteRenderOptionState,
                        ) => {
                          const meta = getEntityMeta(option);
                          const isSelected = state.selected;
                          return (
                            <li
                              {...props}
                              style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '8px',
                                padding: '12px 16px',
                                cursor: 'pointer',
                                borderBottom:
                                  state.index ===
                                  filteredRelationshipEntities.length - 1
                                    ? 'none'
                                    : '1px solid var(--secondary-color)',
                                backgroundColor: isSelected
                                  ? 'rgba(72, 149, 239, 0.12)'
                                  : 'transparent',
                              }}
                            >
                              <div>
                                <div style={{ color: 'var(--text-primary)' }}>
                                  {option.name}
                                </div>
                                {meta && (
                                  <div
                                    style={{
                                      color:
                                        'var(--Text-Muted, var(--card-text))',
                                      marginTop: '2px',
                                      fontSize: '12px',
                                    }}
                                  >
                                    {meta}
                                  </div>
                                )}
                              </div>
                            </li>
                          );
                        }}
                        renderInput={(
                          params: AutocompleteRenderInputParams,
                        ) => (
                          <TextField
                            {...params}
                            placeholder={
                              relationshipTargetKind
                                ? 'Search by name...'
                                : 'Select company or person first'
                            }
                            sx={tariffInputSx}
                            size='small'
                          />
                        )}
                      />
                    </div>
                  )}

                  {isCreateMode && (
                    <>
                      <div>
                        <label
                          htmlFor='create-entity-name'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Name <span className='required-star'>*</span>
                        </label>
                        <TextField
                          id='create-entity-name'
                          value={createForm.name}
                          onChange={handleCreateTextFieldChange('name')}
                          placeholder={
                            relationshipTargetKind === 'person'
                              ? 'Person name'
                              : 'Company name'
                          }
                          sx={tariffInputSx}
                          fullWidth
                          size='small'
                        />
                      </div>

                      <div>
                        <label
                          htmlFor='create-entity-alias'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Alias
                        </label>
                        <TextField
                          type='text'
                          id='create-entity-alias'
                          placeholder='Alias'
                          sx={tariffInputSx}
                          fullWidth
                          size='small'
                          value={createForm.alias}
                          onChange={handleCreateTextFieldChange('alias')}
                        />
                      </div>

                      {relationshipTargetKind === 'person' && (
                        <div>
                          <label
                            htmlFor='create-entity-job-title'
                            className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                          >
                            Job Title
                          </label>
                          <TextField
                            type='text'
                            id='create-entity-job-title'
                            value={createForm.jobTitle}
                            onChange={handleCreateTextFieldChange('jobTitle')}
                            placeholder='Chief Executive Officer'
                            sx={tariffInputSx}
                            fullWidth
                            size='small'
                          />
                        </div>
                      )}

                      <div className='col-span-2'>
                        <label
                          htmlFor='create-entity-description'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Description
                        </label>
                        <TextField
                          id='create-entity-description'
                          rows={3}
                          multiline
                          placeholder='Brief description'
                          value={createForm.description}
                          onChange={handleCreateTextFieldChange('description')}
                          sx={{
                            ...tariffInputSx,
                            '& .MuiOutlinedInput-root': {
                              ...tariffInputSx['& .MuiOutlinedInput-root'],
                              height: 'auto',
                            },
                          }}
                          fullWidth
                          size='small'
                        />
                      </div>

                      <div>
                        <label
                          htmlFor='create-entity-email'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Contact Email
                        </label>
                        <TextField
                          type='email'
                          id='create-entity-email'
                          placeholder='contact@company.com'
                          sx={tariffInputSx}
                          fullWidth
                          size='small'
                          value={createForm.email}
                          onChange={handleCreateTextFieldChange('email')}
                        />
                      </div>

                      <div>
                        <label
                          htmlFor='create-entity-phone'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Contact Phone
                        </label>
                        <TextField
                          type='tel'
                          id='create-entity-phone'
                          placeholder='+1-555-0123'
                          sx={tariffInputSx}
                          fullWidth
                          size='small'
                          value={createForm.phone}
                          onChange={handleCreateTextFieldChange('phone')}
                        />
                      </div>

                      <div>
                        <label
                          htmlFor='create-entity-address'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Address
                        </label>
                        <TextField
                          type='text'
                          id='create-entity-address'
                          placeholder='Street address'
                          sx={tariffInputSx}
                          fullWidth
                          size='small'
                          value={createForm.address}
                          onChange={handleCreateTextFieldChange('address')}
                        />
                      </div>

                      <div>
                        <label
                          htmlFor='create-entity-address1'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Address 1
                        </label>
                        <TextField
                          type='text'
                          id='create-entity-address1'
                          placeholder='Additional address line 1'
                          sx={tariffInputSx}
                          fullWidth
                          size='small'
                          value={createForm.address1}
                          onChange={handleCreateTextFieldChange('address1')}
                        />
                      </div>

                      <div>
                        <label
                          htmlFor='create-entity-address2'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Address 2
                        </label>
                        <TextField
                          type='text'
                          id='create-entity-address2'
                          placeholder='Additional address line 2'
                          sx={tariffInputSx}
                          fullWidth
                          size='small'
                          value={createForm.address2}
                          onChange={handleCreateTextFieldChange('address2')}
                        />
                      </div>

                      <div>
                        <label
                          htmlFor='create-entity-country'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Country
                        </label>
                        <TextField
                          type='text'
                          id='create-entity-country'
                          placeholder='Country'
                          sx={tariffInputSx}
                          fullWidth
                          size='small'
                          value={createForm.country}
                          onChange={handleCreateTextFieldChange('country')}
                        />
                      </div>

                      <div>
                        <label
                          htmlFor='create-entity-business-unit'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Business Unit
                        </label>
                        <TextField
                          type='text'
                          id='create-entity-business-unit'
                          placeholder='Business unit'
                          sx={tariffInputSx}
                          fullWidth
                          size='small'
                          value={createForm.businessUnit}
                          onChange={handleCreateTextFieldChange('businessUnit')}
                        />
                      </div>

                      <div>
                        <label
                          htmlFor='create-entity-parent-business'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Parent Business
                        </label>
                        <TextField
                          type='text'
                          id='create-entity-parent-business'
                          placeholder='Parent business'
                          sx={tariffInputSx}
                          fullWidth
                          size='small'
                          value={createForm.parentBusiness}
                          onChange={handleCreateTextFieldChange(
                            'parentBusiness',
                          )}
                        />
                      </div>

                      <div>
                        <label
                          htmlFor='create-entity-sku-product-id'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          SKU / Product ID
                        </label>
                        <TextField
                          type='text'
                          id='create-entity-sku-product-id'
                          placeholder='SKU or Product ID'
                          sx={tariffInputSx}
                          fullWidth
                          size='small'
                          value={createForm.skuProductId}
                          onChange={handleCreateTextFieldChange('skuProductId')}
                        />
                      </div>

                      <div>
                        <label
                          htmlFor='create-entity-supplier-vendor-id'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Supplier / Vendor ID
                        </label>
                        <TextField
                          type='text'
                          id='create-entity-supplier-vendor-id'
                          placeholder='Supplier or Vendor ID'
                          sx={tariffInputSx}
                          fullWidth
                          size='small'
                          value={createForm.supplierVendorId}
                          onChange={handleCreateTextFieldChange(
                            'supplierVendorId',
                          )}
                        />
                      </div>

                      <div>
                        <label
                          htmlFor='create-entity-business-registration-number'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Business Registration Number / (EIN)
                        </label>
                        <TextField
                          type='text'
                          id='create-entity-business-registration-number'
                          placeholder='Business registration number or EIN'
                          sx={tariffInputSx}
                          fullWidth
                          size='small'
                          value={createForm.businessRegistrationNumber}
                          onChange={handleCreateTextFieldChange(
                            'businessRegistrationNumber',
                          )}
                        />
                      </div>

                      <div>
                        <label
                          htmlFor='create-entity-person-id'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Person ID
                        </label>
                        <TextField
                          type='text'
                          id='create-entity-person-id'
                          placeholder='Person identifier'
                          sx={tariffInputSx}
                          fullWidth
                          size='small'
                          value={createForm.personId}
                          onChange={handleCreateTextFieldChange('personId')}
                        />
                      </div>
                      <div>
                        <label
                          htmlFor='create-entity-manufacturer-identification-number'
                          className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                        >
                          Manufacturer Identification Number (MID)
                        </label>
                        <TextField
                          type='text'
                          id='create-entity-manufacturer-identification-number'
                          placeholder='Manufacturer identification number'
                          sx={tariffInputSx}
                          fullWidth
                          size='small'
                          value={createForm.manufacturerIdentificationNumber}
                          onChange={handleCreateTextFieldChange(
                            'manufacturerIdentificationNumber',
                          )}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
      <div
        style={{
          width: '100%',
          padding: '0 24px',
          marginTop: '12px',
          marginBottom: '12px',
          color: 'var(--Text-Muted, var(--card-text))',
          fontSize: '12px',
        }}
      >
        {relationshipPreview}
      </div>
      <div className='audit-modal-cont-footer'>
        <div className='audit-modal-cont-footer-content'>
          <div
            className='audit-modal-cont-footer-content-btn-cancel'
            onClick={handleClose}
          >
            <div className='audit-modal-cont-footer-content-btn-text'>
              Cancel
            </div>
          </div>
          <div
            className={`audit-modal-cont-footer-content-btn-upload${
              canSaveRelationship ? '' : ' opacity-50 cursor-not-allowed'
            }`}
            onClick={canSaveRelationship ? handleSaveRelationship : undefined}
          >
            <div className='audit-modal-cont-footer-content-btn-text'>
              {isRelationshipSaving ? (
                <span className='flex items-center justify-center'>
                  <CircularProgress
                    style={{
                      color: 'var(--text-primary)',
                      width: '20px',
                      height: '20px',
                      marginRight: '10px',
                    }}
                  />
                  {isCreateMode ? 'Creating...' : 'Saving...'}
                </span>
              ) : isCreateMode ? (
                'Create & Link Relationship'
              ) : (
                'Save Relationship'
              )}
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default AddEntityRelationshipModal;
